import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FaSyncAlt, FaTimes } from "react-icons/fa";
import api from "../../utils/api";
import { formatDateTimeIST, IST_TIMEZONE } from "../../utils/dateTime";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminSearchInput,
  AdminSelect,
  AdminTableCard,
  AdminTableState,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";

type CaseCounts = {
  allocated_designer: number;
  allocated_qc: number;
  designed_success: number;
  qc_success: number;
};

type CaseRole = "Designed" | "QC2" | "Allocated designer" | "Allocated QC";
type CaseWorkflowStatus = "Allocated" | "QC1" | "Designed" | "QC2 done";

type EmpCaseRow = {
  allocation_id: number;
  case_id: string;
  kind: "live" | "redesign";
  roles: CaseRole[];
  status: CaseWorkflowStatus;
  case_units: number;
  allocated_units: number;
  actual_units: number;
  tentative: boolean;
};

type ShiftWork = { emp_name: string; role: "Designed" | "QC2" };

type ShiftCaseRow = {
  allocation_id: number;
  case_id: string;
  kind: "live" | "redesign";
  status: string;
  case_units: number;
  work: ShiftWork[];
};

type EmpRow = {
  emp_id: number;
  emp_name: string;
  role: string;
  shift: string;
  punch_in: string | null;
  punch_out: string | null;
  punched_in: boolean;
  still_in: boolean;
  work_from: string;
  work_to: string;
  live: CaseCounts;
  redesign: CaseCounts;
  hold: number;
  transferred_designer: number;
  transferred_qc: number;
  designed_units_done: number;
  designed_units_tentative: number;
  designed_units: number;
  qc_units_done: number;
  qc_units_tentative: number;
  qc_units: number;
  subtotal_units: number;
  allocated_units: number;
  actual_units: number;
  cases: EmpCaseRow[];
};

type ShiftBlock = {
  shift_id: number;
  shift_name: string;
  start_time: string;
  end_time: string;
  window_start: string;
  window_end: string;
  is_current: boolean;
  employees: EmpRow[];
  cases?: ShiftCaseRow[];
  totals: Omit<
    EmpRow,
    | "emp_id"
    | "emp_name"
    | "role"
    | "shift"
    | "punch_in"
    | "punch_out"
    | "punched_in"
    | "still_in"
    | "work_from"
    | "work_to"
    | "cases"
  >;
};

type HistoryPayload = {
  generated_at: string;
  timezone: string;
  shifts: ShiftBlock[];
};

const COL_COUNT = 18;

const formatPunch = (value: string | null, stillIn?: boolean) => {
  if (stillIn && !value) return "In shift";
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("en-IN", {
    timeZone: IST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const sameShift = (left?: string | null, right?: string | null) =>
  String(left || "")
    .trim()
    .toLowerCase() ===
  String(right || "")
    .trim()
    .toLowerCase();

const formatWindow = (start: string, end: string) => {
  const startLabel = formatDateTimeIST(start);
  const endLabel = formatDateTimeIST(end);
  return `${startLabel} → ${endLabel}`;
};

const CountCell = ({ value }: { value: number }) => (
  <td className={`${adminTableCell} text-center tabular-nums whitespace-nowrap`}>
    {value}
  </td>
);

const UnitsCell = ({
  total,
  done,
  tentative,
  emphasize,
}: {
  total: number;
  done: number;
  tentative: number;
  emphasize?: boolean;
}) => (
  <td
    className={`${adminTableCell} text-right whitespace-nowrap ${
      emphasize ? "font-semibold text-[#1A1A18]" : ""
    }`}
  >
    <div className="tabular-nums leading-tight">{total}</div>
    {tentative > 0 ? (
      <div className="text-[10px] text-amber-700 font-medium leading-tight">
        {done} done · {tentative}* tent.
      </div>
    ) : null}
  </td>
);

const statusClass = (status: string) => {
  const label = status.toLowerCase();
  if (label.startsWith("hold")) return "bg-rose-100 text-rose-800";
  if (
    label === "qc2 done" ||
    label === "completed" ||
    label === "uploaded" ||
    label === "ready to upload"
  ) {
    return "bg-emerald-100 text-emerald-800";
  }
  if (label === "designed") return "bg-amber-100 text-amber-800";
  if (label === "qc1") return "bg-sky-100 text-sky-800";
  return "bg-[#E8E8E4] text-[#4A4A48]";
};

const roleClass = (role: CaseRole) => {
  if (role === "Designed") return "bg-amber-50 text-amber-800 ring-1 ring-amber-200";
  if (role === "QC2") return "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200";
  return "bg-[#F4F4F1] text-[#4A4A48] ring-1 ring-[#E8E8E4]";
};

const EmployeeCasesModal = ({
  shift,
  row,
  onClose,
}: {
  shift: ShiftBlock;
  row: EmpRow;
  onClose: () => void;
}) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cases = (row.cases || []).filter(
    (item) => item.roles.length > 0 && sameShift(row.shift, shift.shift_name),
  );

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/45 flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E8E8E4]">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-[#1A1A18] truncate">
              {row.emp_name}
            </h3>
            <p className="text-xs text-[#6B6B66] mt-0.5">
              {row.role} · {shift.shift_name} only · punch{" "}
              {formatPunch(row.punch_in)} –{" "}
              {row.still_in ? "In shift" : formatPunch(row.punch_out)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-[#8A8A85] hover:bg-[#F4F4F1] hover:text-[#4A4A48] inline-flex items-center justify-center"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-5 py-3 flex flex-wrap gap-2 text-xs border-b border-[#E8E8E4]">
          <span className="rounded-full bg-[#E7F6F8] px-3 py-1 font-medium text-[#1F6F7A]">
            {shift.shift_name}
          </span>
          <span className="rounded-full bg-[#F4F6F5] px-3 py-1 font-medium text-[#345952]">
            {cases.length} case{cases.length === 1 ? "" : "s"}
          </span>
          <span className="rounded-full bg-[#F4F6F5] px-3 py-1 font-medium text-[#345952]">
            Allocated units {row.allocated_units || 0}
          </span>
          <span className="rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-800">
            Actual units {row.actual_units || 0}
          </span>
          <span className="text-[#8A8A85] self-center">
            Only {row.emp_name}&apos;s cases on {shift.shift_name}. Allocated =
            pending + designed + QC2. Actual = designed + QC2.
          </span>
        </div>

        <div className="overflow-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead className="sticky top-0 bg-[#F8F8F6]">
              <tr className="border-b border-[#E8E8E4]">
                {[
                  "Case",
                  "Type",
                  "Role on case",
                  "Status",
                  "Case units",
                  "Allocated units",
                  "Actual units",
                ].map((label) => (
                  <th key={label} className={`${adminTableHead} whitespace-nowrap`}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!cases.length ? (
                <AdminTableState
                  colSpan={7}
                  variant="empty"
                  message={`No cases for ${row.emp_name} on ${shift.shift_name}.`}
                />
              ) : (
                cases.map((item) => (
                  <tr
                    key={item.allocation_id}
                    className="border-b border-[#F0F0EC]"
                  >
                    <td className={`${adminTableCell} font-medium whitespace-nowrap`}>
                      {item.case_id}
                    </td>
                    <td className={`${adminTableCell} capitalize`}>
                      {item.kind}
                    </td>
                    <td className={adminTableCell}>
                      <div className="flex flex-wrap gap-1">
                        {item.roles.length ? (
                          item.roles.map((role) => (
                            <span
                              key={role}
                              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${roleClass(role)}`}
                            >
                              {role}
                            </span>
                          ))
                        ) : (
                          <span className="text-[#8A8A85]">—</span>
                        )}
                      </div>
                    </td>
                    <td className={adminTableCell}>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(item.status)}`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className={`${adminTableCell} tabular-nums`}>
                      {item.case_units}
                      {item.tentative ? (
                        <span className="ml-1 text-[10px] text-amber-700">*</span>
                      ) : null}
                    </td>
                    <td className={`${adminTableCell} tabular-nums`}>
                      {item.allocated_units}
                    </td>
                    <td className={`${adminTableCell} tabular-nums font-medium`}>
                      {item.actual_units}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ShiftCasesModal = ({
  shift,
  onClose,
}: {
  shift: ShiftBlock;
  onClose: () => void;
}) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cases = shift.cases || [];

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/45 flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#E8E8E4]">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-[#1A1A18] truncate">
              {shift.shift_name}
            </h3>
            <p className="text-xs text-[#6B6B66] mt-0.5">
              {shift.is_current ? "Current shift" : "Previous shift"} ·{" "}
              {shift.start_time} – {shift.end_time} ·{" "}
              {formatWindow(shift.window_start, shift.window_end)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-[#8A8A85] hover:bg-[#F4F4F1] hover:text-[#4A4A48] inline-flex items-center justify-center"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-5 py-3 flex flex-wrap gap-2 text-xs border-b border-[#E8E8E4]">
          <span className="rounded-full bg-[#F4F6F5] px-3 py-1 font-medium text-[#345952]">
            {cases.length} case{cases.length === 1 ? "" : "s"}
          </span>
          <span className="text-[#8A8A85] self-center">
            Design or QC2 finished inside this shift window. Status is where
            the case is now.
          </span>
        </div>

        <div className="overflow-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead className="sticky top-0 bg-[#F8F8F6]">
              <tr className="border-b border-[#E8E8E4]">
                {["Case", "Type", "Work in this shift", "Current status", "Case units"].map(
                  (label) => (
                    <th key={label} className={`${adminTableHead} whitespace-nowrap`}>
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {!cases.length ? (
                <AdminTableState
                  colSpan={5}
                  variant="empty"
                  message="No design or QC2 work in this shift window."
                />
              ) : (
                cases.map((item) => (
                  <tr
                    key={item.allocation_id}
                    className="border-b border-[#F0F0EC]"
                  >
                    <td className={`${adminTableCell} font-medium whitespace-nowrap`}>
                      {item.case_id}
                    </td>
                    <td className={`${adminTableCell} capitalize`}>{item.kind}</td>
                    <td className={adminTableCell}>
                      <div className="flex flex-wrap gap-1">
                        {item.work.map((work) => (
                          <span
                            key={`${work.role}-${work.emp_name}`}
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${roleClass(work.role)}`}
                          >
                            {work.role}
                            {work.emp_name && work.emp_name !== "—"
                              ? ` · ${work.emp_name}`
                              : ""}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className={adminTableCell}>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(item.status)}`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className={`${adminTableCell} tabular-nums`}>
                      {item.case_units}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ShiftHistory = () => {
  const [data, setData] = useState<HistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [previous, setPrevious] = useState("2");
  const [open, setOpen] = useState<{ shift: ShiftBlock; row: EmpRow } | null>(
    null,
  );
  const [shiftCases, setShiftCases] = useState<ShiftBlock | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/allocation-tool/shift-history", {
        params: { previous },
      });
      setData(res.data.data as HistoryPayload);
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response
                ?.data?.message || "Failed to load shift history",
            )
          : "Failed to load shift history";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [previous]);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const shifts = useMemo(() => {
    const list = data?.shifts || [];
    if (!query) return list;
    return list.map((shift) => ({
      ...shift,
      employees: shift.employees.filter((row) =>
        `${row.emp_name} ${row.role}`.toLowerCase().includes(query),
      ),
    }));
  }, [data?.shifts, query]);

  return (
    <>
      <Helmet>
        <title>Allocation History</title>
      </Helmet>
      <div className="min-h-screen w-full bg-[#F4F6F5] p-3 md:p-5">
        <AdminPageShell>
          <AdminPageHeader
            title="Shift allocation history"
            subtitle="Current shift plus the previous two by default. Use the selector to look back up to the last four. Counts use each employee's punch-in to punch-out window."
          >
            <AdminSelect
              value={previous}
              onChange={setPrevious}
              className="w-full sm:w-[200px]"
            >
              <option value="2">Current + last 2</option>
              <option value="3">Current + last 3</option>
              <option value="4">Current + last 4</option>
            </AdminSelect>
            <AdminSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search employee…"
              className="w-full sm:w-[220px]"
            />
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/15 border border-white/25 text-white px-4 py-2.5 text-xs font-medium hover:bg-white/20 transition disabled:opacity-60"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </AdminPageHeader>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#6B6B66] px-0.5">
            <span>
              Generated{" "}
              {data?.generated_at ? formatDateTimeIST(data.generated_at) : "—"}{" "}
              IST
            </span>
            <span>
              Designer and QC units come from the job card when marked.
              If those are missing, system-calculated case units are shown as
              tentative*. An employee name opens only that person’s cases on
              that shift. All cases lists every case designed or QC2’d in the
              shift, with the status the case has now.
            </span>
            <span>
              Transferred = allocated to them, but designed or QC2 was done by
              someone else.
            </span>
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          ) : null}

          {loading && !data ? (
            <AdminTableCard>
              <table className="w-full">
                <tbody>
                  <AdminTableState
                    colSpan={COL_COUNT}
                    variant="loading"
                    message="Loading shift history…"
                  />
                </tbody>
              </table>
            </AdminTableCard>
          ) : null}

          {!loading && !error && !shifts.length ? (
            <AdminTableCard>
              <table className="w-full">
                <tbody>
                  <AdminTableState
                    colSpan={COL_COUNT}
                    variant="empty"
                    message="No shifts found. Add shifts in the shifts table first."
                  />
                </tbody>
              </table>
            </AdminTableCard>
          ) : null}

          {shifts.map((shift) => (
            <section key={`${shift.shift_id}-${shift.window_start}`} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2 px-0.5">
                <h2 className="text-base font-semibold text-[#1A1A18]">
                  {shift.shift_name}
                </h2>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    shift.is_current
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-[#E8E8E4] text-[#4A4A48]"
                  }`}
                >
                  {shift.is_current ? "Current shift" : "Previous shift"}
                </span>
                <span className="text-xs text-[#6B6B66]">
                  {shift.start_time} – {shift.end_time}
                  {" · "}
                  {formatWindow(shift.window_start, shift.window_end)}
                </span>
                <span className="text-xs text-[#6B6B66]">
                  {shift.employees.length} employee
                  {shift.employees.length === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  onClick={() => setShiftCases(shift)}
                  className="inline-flex items-center rounded-full bg-[#1A1A18] text-white px-3 py-1 text-[11px] font-semibold hover:bg-[#345952] transition"
                >
                  All cases
                  {shift.cases ? ` · ${shift.cases.length}` : ""}
                </button>
              </div>

              <AdminTableCard>
                <table className="min-w-[1280px] w-full border-collapse">
                  <thead>
                    <tr className="bg-[#F8F8F6] border-b border-[#E8E8E4]">
                      <th
                        rowSpan={2}
                        className={`${adminTableHead} sticky left-0 z-20 bg-[#F8F8F6] min-w-[160px]`}
                      >
                        Employee
                      </th>
                      <th
                        colSpan={2}
                        className={`${adminTableHead} text-center border-l border-[#E8E8E4]`}
                      >
                        Punch
                      </th>
                      <th
                        colSpan={4}
                        className={`${adminTableHead} text-center border-l border-[#E8E8E4]`}
                      >
                        Live cases
                      </th>
                      <th
                        colSpan={4}
                        className={`${adminTableHead} text-center border-l border-[#E8E8E4]`}
                      >
                        Redesign cases
                      </th>
                      <th
                        colSpan={3}
                        className={`${adminTableHead} text-center border-l border-[#E8E8E4]`}
                      >
                        Other
                      </th>
                      <th
                        colSpan={3}
                        className={`${adminTableHead} text-center border-l border-[#E8E8E4]`}
                      >
                        Units (live + redesign)
                      </th>
                    </tr>
                    <tr className="bg-[#F8F8F6] border-b border-[#E8E8E4]">
                      {[
                        "In",
                        "Out",
                        "Alloc D",
                        "Alloc QC",
                        "Designed",
                        "QC success",
                        "Alloc D",
                        "Alloc QC",
                        "Designed",
                        "QC success",
                        "Hold",
                        "Xfer D",
                        "Xfer QC",
                        "Designed",
                        "QC",
                        "Subtotal",
                      ].map((label, index) => (
                        <th
                          key={`${label}-${index}`}
                          className={`${adminTableHead} text-center whitespace-nowrap ${
                            index === 0 || index === 2 || index === 6 || index === 10 || index === 13
                              ? "border-l border-[#E8E8E4]"
                              : ""
                          }`}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!shift.employees.length ? (
                      <AdminTableState
                        colSpan={COL_COUNT}
                        variant="empty"
                        message={
                          query
                            ? "No employees match this search."
                            : "No designer / captain employees on this shift."
                        }
                      />
                    ) : (
                      shift.employees.map((row) => (
                        <tr
                          key={row.emp_id}
                          className="border-b border-[#F0F0EC] hover:bg-[#F8F8F6]"
                        >
                          <td
                            className={`${adminTableCell} sticky left-0 z-10 bg-white min-w-[160px]`}
                          >
                            <button
                              type="button"
                              onClick={() => setOpen({ shift, row })}
                              className="font-medium text-[#1A1A18] leading-tight text-left hover:text-[#2fa9bf] hover:underline"
                            >
                              {row.emp_name}
                            </button>
                            <div className="text-[11px] text-[#8A8A85]">
                              {row.role}
                            </div>
                          </td>
                          <td className={`${adminTableCell} text-xs whitespace-nowrap border-l border-[#F0F0EC]`}>
                            {formatPunch(row.punch_in)}
                          </td>
                          <td className={`${adminTableCell} text-xs whitespace-nowrap`}>
                            {row.still_in
                              ? "In shift"
                              : formatPunch(row.punch_out)}
                          </td>
                          <CountCell value={row.live.allocated_designer} />
                          <CountCell value={row.live.allocated_qc} />
                          <CountCell value={row.live.designed_success} />
                          <CountCell value={row.live.qc_success} />
                          <CountCell value={row.redesign.allocated_designer} />
                          <CountCell value={row.redesign.allocated_qc} />
                          <CountCell value={row.redesign.designed_success} />
                          <CountCell value={row.redesign.qc_success} />
                          <CountCell value={row.hold} />
                          <CountCell value={row.transferred_designer} />
                          <CountCell value={row.transferred_qc} />
                          <UnitsCell
                            total={row.designed_units}
                            done={row.designed_units_done}
                            tentative={row.designed_units_tentative}
                          />
                          <UnitsCell
                            total={row.qc_units}
                            done={row.qc_units_done}
                            tentative={row.qc_units_tentative}
                          />
                          <UnitsCell
                            total={row.subtotal_units}
                            done={
                              row.designed_units_done + row.qc_units_done
                            }
                            tentative={
                              row.designed_units_tentative +
                              row.qc_units_tentative
                            }
                            emphasize
                          />
                        </tr>
                      ))
                    )}
                    {shift.employees.length ? (
                      <tr className="bg-[#F8F8F6] font-semibold">
                        <td
                          className={`${adminTableCell} sticky left-0 z-10 bg-[#F8F8F6]`}
                        >
                          Shift total
                        </td>
                        <td className={`${adminTableCell} border-l border-[#E8E8E4]`} colSpan={2} />
                        <CountCell value={shift.totals.live.allocated_designer} />
                        <CountCell value={shift.totals.live.allocated_qc} />
                        <CountCell value={shift.totals.live.designed_success} />
                        <CountCell value={shift.totals.live.qc_success} />
                        <CountCell value={shift.totals.redesign.allocated_designer} />
                        <CountCell value={shift.totals.redesign.allocated_qc} />
                        <CountCell value={shift.totals.redesign.designed_success} />
                        <CountCell value={shift.totals.redesign.qc_success} />
                        <CountCell value={shift.totals.hold} />
                        <CountCell value={shift.totals.transferred_designer} />
                        <CountCell value={shift.totals.transferred_qc} />
                        <UnitsCell
                          total={shift.totals.designed_units}
                          done={shift.totals.designed_units_done}
                          tentative={shift.totals.designed_units_tentative}
                        />
                        <UnitsCell
                          total={shift.totals.qc_units}
                          done={shift.totals.qc_units_done}
                          tentative={shift.totals.qc_units_tentative}
                        />
                        <UnitsCell
                          total={shift.totals.subtotal_units}
                          done={
                            shift.totals.designed_units_done +
                            shift.totals.qc_units_done
                          }
                          tentative={
                            shift.totals.designed_units_tentative +
                            shift.totals.qc_units_tentative
                          }
                          emphasize
                        />
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </AdminTableCard>
            </section>
          ))}
        </AdminPageShell>
      </div>
      {open ? (
        <EmployeeCasesModal
          shift={open.shift}
          row={open.row}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {shiftCases ? (
        <ShiftCasesModal
          shift={shiftCases}
          onClose={() => setShiftCases(null)}
        />
      ) : null}
    </>
  );
};

export default ShiftHistory;
